import { useState } from 'react'
import { inject, observer } from 'mobx-react'

import iconClose from '../../../assets/icons/IconCloseSVG'
import {
  Button,
  Drawer,
  NumberInput,
  RetirementIncomeValues,
  Slider,
  SVGWrapper,
  TextErrorField,
} from '../../../components'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../../models/Person'
import { includesRetiredSpouse, projectedIncomeValues } from '../../../utils'
import CouldYouLiveHelp from './CouldYouLiveHelp'
import IncomeReplacementOptionCard from './IncomeReplacementOptionCard'
import IncomeReplacementTellMeMore, { SEEM_LOW_NOTE } from './IncomeReplacementTellMeMore'
import {
  Content,
  HeaderBlock,
  HeaderLabelRow,
  CloseButton,
  Heading,
  HeadingEmphasis,
  CardRow,
  ValuesBox,
  ValuesHeading,
  CustomControls,
  CustomBlock,
  PercentField,
  PercentLabel,
  PercentSuffix,
  SliderBox,
  Footer,
} from './IncomeReplacementDrawerStyle'

const MIN_PERCENT = 5
// The input can exceed the slider maximum.
const SLIDER_MAX_PERCENT = 150
const PERCENT_STEP = 5
const MIN_PERCENT_ERROR = `Please plan for an income replacement goal of at least ${MIN_PERCENT}%`

const PRESET_HEADING = 'Could you live on this amount?'
const CUSTOM_HEADING = 'How much would you like to be able to spend in retirement?'

/** Percentages of current after-tax income. */
const GOAL_PRESETS = [
  { title: 'Lower costs', percent: 70 },
  { title: 'Similar to today', percent: 80 },
  { title: 'Higher costs', percent: 90 },
]

const presetForPercent = percent => GOAL_PRESETS.find(preset => preset.percent === percent) ?? null

/** Pass `source` to read from a store other than the injected one, like onboarding does. */
const IncomeReplacementDrawer = ({ active, toggleDrawer, handleSave, source, store }) => {
  const dataSource = source ?? store
  const { baseCase, person, spouse } = dataSource
  // Same nudge the income goal page carries: with a retired spouse the amount
  // covers one income, and Tell me more below is where that is explained.
  const spouseRetired = includesRetiredSpouse({ person, spouse })
  const savedPercent = person?.retirementIncomeGoalPct ?? DEFAULT_RETIREMENT_INCOME_GOAL_PCT

  // Null until the person picks something, so an untouched drawer follows the saved goal.
  const [chosenGoal, setChosenGoal] = useState(null)
  const [showValueAtRetirement, setShowValueAtRetirement] = useState(false)
  const [showTellMeMore, setShowTellMeMore] = useState(false)
  const [previousActive, setPreviousActive] = useState(active)

  // Reset in render, not an effect, so a reopened drawer never paints the abandoned edit.
  if (previousActive !== active) {
    setPreviousActive(active)

    if (active) {
      setChosenGoal(null)
      setShowValueAtRetirement(false)
      setShowTellMeMore(false)
    }
  }

  // Non-preset saved values open as Custom.
  const selectedPreset = chosenGoal ? chosenGoal.preset : presetForPercent(savedPercent)
  const customInput = chosenGoal ? chosenGoal.customInput : savedPercent
  const isCustom = selectedPreset === null
  const customPercent = customInput ?? 0
  const selectedPercent = isCustom ? customPercent : selectedPreset.percent

  const chooseGoal = (preset, input = customInput) => setChosenGoal({ preset, customInput: input })

  const monthlyFor = percent =>
    projectedIncomeValues({ retirementIncomeGoalPct: percent, baseCase }).todaysMonthly

  const values = projectedIncomeValues({ retirementIncomeGoalPct: selectedPercent, baseCase })

  const handleCustomInput = (name, value) => chooseGoal(null, Number.isFinite(value) ? value : null)

  const isBelowMinimum = customInput == null || customInput < MIN_PERCENT
  const isSaveDisabled = (isCustom && isBelowMinimum) || selectedPercent === savedPercent

  return (
    <Drawer
      width='62.5%'
      /* 62.5% of the design's 1280 frame; capped so wider screens do not stretch it. */
      maxWidth='800px'
      title={
        <HeaderBlock>
          <HeaderLabelRow>
            Edit income after retiring
            <CloseButton type='button' aria-label='Close' onClick={toggleDrawer}>
              <SVGWrapper svg={iconClose} size='large' />
            </CloseButton>
          </HeaderLabelRow>

          <Heading>
            Do you expect your <HeadingEmphasis>costs in retirement</HeadingEmphasis> to be lower,
            higher or the same as today?
          </Heading>
        </HeaderBlock>
      }
      active={active}
      onOverlayClick={toggleDrawer}
      onEscKeyDown={toggleDrawer}
      padding='24px 32px'
      mobilePadding='24px 0'
      responsiveWidth>
      <Content>
        <CardRow>
          {GOAL_PRESETS.map(preset => (
            <IncomeReplacementOptionCard
              key={preset.percent}
              title={preset.title}
              percent={preset.percent}
              amount={monthlyFor(preset.percent)}
              selected={preset === selectedPreset}
              onSelect={() => chooseGoal(preset)}
            />
          ))}

          <IncomeReplacementOptionCard
            title='Custom'
            percent={customPercent}
            amount={monthlyFor(customPercent)}
            selected={isCustom}
            hasGoal={isCustom}
            onSelect={() => chooseGoal(null)}
          />
        </CardRow>

        <ValuesBox>
          <ValuesHeading>
            {isCustom ? CUSTOM_HEADING : PRESET_HEADING}
            {!isCustom && <CouldYouLiveHelp />}
          </ValuesHeading>

          {isCustom && (
            <CustomBlock>
              <CustomControls>
                <PercentField>
                  <NumberInput
                    name='percent'
                    width='60px'
                    value={customInput}
                    onChange={handleCustomInput}
                    allowNegative={false}
                    decimalScale={0}
                    fontSize='18px'
                  />
                  <PercentSuffix>%</PercentSuffix>
                  <PercentLabel>of current income</PercentLabel>
                </PercentField>

                <SliderBox>
                  <Slider
                    value={Math.min(Math.max(customPercent, MIN_PERCENT), SLIDER_MAX_PERCENT)}
                    onChange={value => chooseGoal(null, value)}
                    step={PERCENT_STEP}
                    min={MIN_PERCENT}
                    max={SLIDER_MAX_PERCENT}
                  />
                </SliderBox>
              </CustomControls>

              <TextErrorField error={MIN_PERCENT_ERROR} showError={isBelowMinimum} />
            </CustomBlock>
          )}

          <RetirementIncomeValues
            values={values}
            expanded={showValueAtRetirement}
            onToggle={() => setShowValueAtRetirement(shown => !shown)}
            showAtRetirementHelp
            todaysNote={spouseRetired ? SEEM_LOW_NOTE : null}
          />
        </ValuesBox>

        <IncomeReplacementTellMeMore
          percent={selectedPercent}
          expanded={showTellMeMore}
          onToggle={() => setShowTellMeMore(shown => !shown)}
          source={dataSource}
        />

        <Footer>
          <Button variant='secondary' type='button' onClick={toggleDrawer}>
            Cancel
          </Button>
          <Button
            variant='primary'
            type='button'
            disabled={isSaveDisabled}
            onClick={() => handleSave(selectedPercent)}>
            Save &amp; recalculate
          </Button>
        </Footer>
      </Content>
    </Drawer>
  )
}

export default inject('store')(observer(IncomeReplacementDrawer))
